import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Wallet, Loader2, RefreshCw, Download, CalendarDays, History, ChevronDown, ChevronUp, Users, CheckCircle, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const exportPayrollCsv = (payroll) => {
  if (!payroll || !payroll.workers?.length) {
    toast.info('No worker pay to export for this payday');
    return;
  }
  const rows = [
    ['Payday', payroll.payday],
    ['Pay window', `${payroll.window_start} to ${payroll.window_end}`],
    [],
    ['Worker', 'Maps', 'Total'],
    ...payroll.workers.map((w) => [w.name, w.session_count, w.total.toFixed(2)]),
    [],
    ['TOTAL', payroll.workers.reduce((a, w) => a + w.session_count, 0), (payroll.total || 0).toFixed(2)],
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payroll-${payroll.payday}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported payroll-${payroll.payday}.csv`);
};

const WorkerTable = ({ workers, prefix }) => (
  <div className="space-y-1.5">
    {workers.map((w) => (
      <div key={w.user_id} className="flex items-center justify-between text-sm border-b border-gray-100 last:border-0 pb-1.5 last:pb-0"
        data-testid={`${prefix}-worker-row-${w.user_id}`}>
        <span className="text-gray-800 truncate">
          {w.name}
          <span className="text-xs text-gray-400 ml-1.5">{w.session_count} map{w.session_count === 1 ? '' : 's'}</span>
        </span>
        <span className="font-semibold text-gray-900 whitespace-nowrap">{fmt(w.total)}</span>
      </div>
    ))}
  </div>
);

const PaidBadge = ({ paidAt, paidBy }) => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
    title={`Marked paid ${paidAt ? new Date(paidAt).toLocaleString() : ''}${paidBy ? ` by ${paidBy}` : ''}`}
  >
    <CheckCircle size={11} /> Paid
  </span>
);

const PaydayHistoryRow = ({ payday, onMarkPaid, onUnmarkPaid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg bg-white" data-testid={`payday-history-row-${payday.payday}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        data-testid={`payday-history-toggle-${payday.payday}`}
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
            {payday.payday_label}
            {payday.paid && <PaidBadge paidAt={payday.paid_at} paidBy={payday.paid_by_name} />}
          </p>
          <p className="text-xs text-gray-400">
            Maps completed {payday.window_start} → {payday.window_end} · {payday.worker_count} worker{payday.worker_count === 1 ? '' : 's'} · {payday.session_count} map{payday.session_count === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex items-center gap-3 whitespace-nowrap">
          <span className="text-base font-bold text-gray-900" data-testid={`payday-history-total-${payday.payday}`}>{fmt(payday.total)}</span>
          {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100">
          {payday.workers.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No worker pay for this payday.</p>
          ) : (
            <WorkerTable workers={payday.workers} prefix={`history-${payday.payday}`} />
          )}
          <div className="flex items-center gap-2 mt-3">
            {payday.workers.length > 0 && (
              <Button
                variant="outline" size="sm"
                onClick={() => exportPayrollCsv(payday)}
                data-testid={`payday-history-export-${payday.payday}`}
              >
                <Download size={14} className="mr-1.5" /> Export CSV
              </Button>
            )}
            {payday.paid ? (
              <Button
                variant="outline" size="sm"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => onUnmarkPaid(payday.payday)}
                data-testid={`payday-unmark-paid-${payday.payday}`}
                title="Undo — removes the frozen paid snapshot"
              >
                <Undo2 size={14} className="mr-1.5" /> Undo Paid
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => onMarkPaid(payday.payday)}
                data-testid={`payday-mark-paid-${payday.payday}`}
                title="Freeze this payday's payroll as what actually went out"
              >
                <CheckCircle size={14} className="mr-1.5" /> Mark as Paid
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminPayroll() {
  const { getAuthHeader } = useAuth();
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [o, h] = await Promise.all([
        axios.get(`${API}/admin/payroll-overview`, getAuthHeader()),
        axios.get(`${API}/admin/payroll-history?count=12`, getAuthHeader()),
      ]);
      setOverview(o.data);
      setHistory(h.data);
    } catch {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markPaid = async (payday) => {
    if (!window.confirm(`Mark payday ${payday} as paid?\n\nThis freezes the payroll snapshot so history shows exactly what went out.`)) return;
    try {
      await axios.put(`${API}/admin/payroll/paydays/${payday}/mark-paid`, {}, getAuthHeader());
      toast.success(`Payday ${payday} marked as paid`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark payday as paid');
    }
  };

  const unmarkPaid = async (payday) => {
    if (!window.confirm(`Undo paid status for payday ${payday}?`)) return;
    try {
      await axios.put(`${API}/admin/payroll/paydays/${payday}/unmark-paid`, {}, getAuthHeader());
      toast.success(`Payday ${payday} is no longer marked as paid`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo paid status');
    }
  };

  const payroll = overview?.upcoming_payroll;

  return (
    <div className="p-6 space-y-6" data-testid="admin-payroll-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet size={22} className="text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Payroll</h1>
        </div>
        <button type="button" onClick={() => { setLoading(true); fetchAll(); }} data-testid="payroll-page-refresh-btn"
          className="text-gray-400 hover:text-gray-600" title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-10 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading payroll…
        </div>
      ) : (
        <>
          {/* Payroll Export — upcoming payday */}
          <Card className="p-6" data-testid="payroll-export-section">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CalendarDays size={18} className="text-emerald-600" /> Payroll Export
                </h2>
                {payroll && (
                  <p className="text-xs text-gray-400 mt-1">
                    Upcoming payday {payroll.payday_label} · maps completed {payroll.window_start} → {payroll.window_end}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {payroll?.paid && <PaidBadge paidAt={payroll.paid_at} paidBy={payroll.paid_by_name} />}
                {payroll && (
                  <span className="text-2xl font-bold text-emerald-700" data-testid="payroll-export-total">{fmt(payroll.total)}</span>
                )}
                {payroll?.can_mark_paid && !payroll?.paid && (
                  <Button
                    onClick={() => markPaid(payroll.payday)}
                    variant="outline"
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    data-testid="payroll-upcoming-mark-paid-btn"
                    title="Today is payday — freeze this payroll as paid"
                  >
                    <CheckCircle size={15} className="mr-1.5" /> Mark as Paid
                  </Button>
                )}
                <Button
                  onClick={() => exportPayrollCsv(payroll)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="payroll-export-csv-btn"
                >
                  <Download size={15} className="mr-1.5" /> Export CSV
                </Button>
              </div>
            </div>
            {!payroll || payroll.workers.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm flex flex-col items-center gap-2">
                <Users size={20} />
                No cleared worker pay for the upcoming payday yet.
              </div>
            ) : (
              <WorkerTable workers={payroll.workers} prefix="upcoming" />
            )}
          </Card>

          {/* Payday History */}
          <Card className="p-6" data-testid="payday-history-section">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <History size={18} className="text-blue-600" /> Payday History
            </h2>
            {!history || history.paydays.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No past paydays found.</div>
            ) : (
              <div className="space-y-2">
                {history.paydays.map((p) => (
                  <PaydayHistoryRow key={p.payday} payday={p} onMarkPaid={markPaid} onUnmarkPaid={unmarkPaid} />
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
